import React, { useContext, useEffect, useState } from 'react';
import { LeafyGreen } from 'lucide-react';
import { themes, Theme } from '../utils/themes';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../store/useStore';
import { useAuth } from '../contexts/AuthContext';
import { getDatabase, ref, get } from 'firebase/database';
import ThemeOnboarding from '../components/themes/ThemeOnboarding';
import ThemePathway from '../components/themes/ThemePathway';

export default function Themes() {
  const navigate = useNavigate();
  const { themesProgress } = useContext(UserContext);
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userPreferences, setUserPreferences] = useState<{ interests?: string[], onboardingCompleted?: boolean } | null>(null);
  const [sortedThemes, setSortedThemes] = useState<Theme[]>([]);

  useEffect(() => {
    async function checkPreferences() {
      if (!currentUser?.uid) {
        const guestPrefs = localStorage.getItem('guest_preferences');
        if (guestPrefs) {
          setUserPreferences(JSON.parse(guestPrefs));
        }
        setLoading(false);
        return;
      }

      const db = getDatabase();
      const prefRef = ref(db, `users/${currentUser.uid}/preferences`);
      const snapshot = await get(prefRef);

      if (snapshot.exists()) {
        const prefs = snapshot.val();
        setUserPreferences(prefs);
      }
      setLoading(false);
    }
    checkPreferences();
  }, [currentUser]);

  useEffect(() => {
    if (themes && userPreferences?.interests) {
      // Sort themes based on interests
      const interests = userPreferences.interests;

      const sorted = [...themes].sort((a, b) => {
        const aInterested = interests.includes(a.category);
        const bInterested = interests.includes(b.category);

        if (aInterested && !bInterested) return -1;
        if (!aInterested && bInterested) return 1;
        return 0;
      });

      setSortedThemes(sorted);
    } else {
      setSortedThemes(themes);
    }
  }, [userPreferences]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  // If user hasn't completed onboarding, show onboarding
  if (!userPreferences?.onboardingCompleted) {
    return (
      <ThemeOnboarding
        onComplete={() => {
          // Refresh preferences to trigger re-render and move to pathway
          if (currentUser?.uid) {
             const db = getDatabase();
             const prefRef = ref(db, `users/${currentUser.uid}/preferences`);
             get(prefRef).then(snapshot => {
               if (snapshot.exists()) {
                 setUserPreferences(snapshot.val());
               }
             });
          } else {
             const guestPrefs = localStorage.getItem('guest_preferences');
             if (guestPrefs) {
               setUserPreferences(JSON.parse(guestPrefs));
             }
          }
        }}
      />
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-purple-50 to-white pb-16'>
      <div className="p-6 max-w-7xl mx-auto ">
      <header className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <LeafyGreen className="w-12 h-12 text-purple-600" />
              <h1 className="text-4xl font-bold text-gray-900">Learning Path</h1>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
              Follow your personalized path to master new vocabulary.
            </p>
            <div className="flex justify-center gap-4">
               <button
                 onClick={() => {
                   const firstUncompleted = sortedThemes.find(theme => {
                     const progress = themesProgress?.find(t => t.theme === theme.name)?.progress || 0;
                     const percentage = Math.ceil((progress / (theme.words.length * 20)) * 100) || 0;
                     return percentage < 90;
                   }) || sortedThemes[0];
                   if (firstUncompleted) navigate(`/training?theme=${firstUncompleted.id}`);
                 }}
                 className="bg-purple-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-purple-700 transition-colors shadow-md"
               >
                 Start Training by Path
               </button>
               <button
                 onClick={() => setUserPreferences(prev => prev ? { ...prev, onboardingCompleted: false } : null)}
                 className="bg-white text-purple-600 border-2 border-purple-200 px-6 py-3 rounded-xl font-bold hover:bg-purple-50 transition-colors shadow-sm"
               >
                 Edit Interests
               </button>
            </div>
          </header>

        <ThemePathway themes={sortedThemes} themesProgress={themesProgress || []} />

      </div>
    </div>
  );
}
